/**
 * Slide-in drawer that shows every invoice for a single client with:
 *   • Mark Paid / Mark Unpaid buttons per invoice
 *   • Running ledger totals (opening + debit + credit + closing)
 *   • Download the account ledger as PDF or Excel
 *   • Optional date-range filter for the downloads
 *
 * Fetches from:
 *   GET /clients/{name}/ledger
 *   GET /clients/{name}/ledger.pdf?start=&end=
 *   GET /clients/{name}/ledger.xlsx?start=&end=
 *   POST /invoices/{id}/mark-paid | /mark-unpaid
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmt, API } from "@/lib/api";
import { toast } from "sonner";
import {
    X, Download, FileText, Loader2, Check, Clock, RotateCcw,
    Calendar, TrendingUp, TrendingDown, LineChart,
} from "lucide-react";

export default function ClientLedgerDrawer({ client, onClose }) {
    // `client` is the row shape from GET /clients: { buyer:{name,gstin,...}, invoice_count, total_revenue, ... }
    const name = client?.buyer?.name || "";
    const gstin = client?.buyer?.gstin || "";
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(true);
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [busyId, setBusyId] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/clients/${encodeURIComponent(name)}/ledger`, {
                params: { gstin, start, end },
            });
            setLedger(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load ledger");
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line
    useEffect(() => { if (name) load(); }, [name, gstin, start, end]);

    const downloadUrl = (kind) => {
        const p = new URLSearchParams();
        if (gstin) p.set("gstin", gstin);
        if (start) p.set("start", start);
        if (end)   p.set("end", end);
        return `${API}/clients/${encodeURIComponent(name)}/ledger.${kind}?${p.toString()}`;
    };

    const setPaid = async (invId, paid) => {
        setBusyId(invId);
        try {
            await api.post(`/invoices/${invId}/${paid ? "mark-paid" : "mark-unpaid"}`);
            toast.success(paid ? "Invoice marked paid" : "Invoice marked unpaid");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't update status");
        } finally { setBusyId(""); }
    };

    const totals = ledger?.totals ?? {};
    const outstanding = ledger?.outstanding_by_invoice ?? [];

    const closingTone = useMemo(() => {
        const b = totals.closing_balance ?? 0;
        if (b <= 0.5) return "emerald";
        return "amber";
    }, [totals]);

    return (
        <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm flex justify-end"
             onClick={onClose} data-testid="client-ledger-drawer">
            <div className="w-full max-w-3xl bg-white shadow-2xl overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Account ledger</div>
                        <div className="text-lg font-black text-slate-900 mt-0.5">{name}</div>
                        <div className="text-xs text-slate-500 mt-0.5 hdb-mono">
                            {gstin || "No GSTIN"}{client?.buyer?.state && ` · ${client.buyer.state}`}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900"
                            data-testid="ledger-close">
                        <X size={20}/>
                    </button>
                </div>

                {/* Filter + downloads */}
                <div className="px-5 pt-4 flex items-end justify-between flex-wrap gap-3">
                    <div className="flex items-end gap-2">
                        <div>
                            <label className="hdb-label"><Calendar size={11} className="inline mr-1"/> From</label>
                            <input type="date" className="hdb-input hdb-mono" value={start}
                                   onChange={(e) => setStart(e.target.value)}
                                   data-testid="ledger-start"/>
                        </div>
                        <div>
                            <label className="hdb-label">To</label>
                            <input type="date" className="hdb-input hdb-mono" value={end}
                                   onChange={(e) => setEnd(e.target.value)}
                                   data-testid="ledger-end"/>
                        </div>
                        {(start || end) && (
                            <button onClick={() => { setStart(""); setEnd(""); }}
                                    className="hdb-btn hdb-btn-ghost text-xs">Clear</button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Link to={`/clients/rate-card?name=${encodeURIComponent(name)}&gstin=${encodeURIComponent(gstin)}`}
                              className="hdb-btn hdb-btn-ghost" data-testid="ledger-ratecard-link"
                              onClick={onClose}
                              title="Compare this client's historic rates against current master prices">
                            <LineChart size={13}/> Rate card
                        </Link>
                        <a href={downloadUrl("pdf")} target="_blank" rel="noreferrer"
                           className="hdb-btn hdb-btn-ghost" data-testid="ledger-pdf-btn">
                            <FileText size={13}/> PDF
                        </a>
                        <a href={downloadUrl("xlsx")}
                           className="hdb-btn hdb-btn-primary" data-testid="ledger-xlsx-btn">
                            <Download size={13}/> Excel
                        </a>
                    </div>
                </div>

                {/* KPI strip */}
                {ledger && (
                    <div className="px-5 mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ledger-kpis">
                        <MiniKpi label="Opening" value={fmt(ledger.opening_balance)}/>
                        <MiniKpi label="Invoiced" value={fmt(totals.debit)} icon={<TrendingUp size={12}/>}/>
                        <MiniKpi label="Received" value={fmt(totals.credit)} icon={<TrendingDown size={12}/>} tone="emerald"/>
                        <MiniKpi label="Closing" value={fmt(totals.closing_balance)} tone={closingTone} bold/>
                    </div>
                )}

                {/* Outstanding-per-invoice table with mark-paid buttons */}
                <div className="px-5 mt-5 pb-8">
                    <div className="hdb-label mb-2">Invoices &amp; payment status</div>
                    <div className="hdb-card overflow-hidden">
                        {loading ? (
                            <div className="p-6 text-center text-slate-400" data-testid="ledger-loading">
                                <Loader2 className="inline animate-spin mr-2" size={14}/> Loading ledger…
                            </div>
                        ) : outstanding.length === 0 ? (
                            <div className="p-6 text-center text-slate-400">No invoices for this client.</div>
                        ) : (
                            <table className="hdb-table w-full">
                                <thead><tr>
                                    <th>Invoice #</th><th>Date</th>
                                    <th className="text-right">Grand Total</th>
                                    <th className="text-right">Paid</th>
                                    <th className="text-right">Balance</th>
                                    <th>Status</th>
                                    <th className="text-right">Action</th>
                                </tr></thead>
                                <tbody>
                                    {outstanding.map((r) => {
                                        const paid = r.status === "paid" || r.balance <= 0.5;
                                        const partial = !paid && r.paid > 0.5;
                                        return (
                                            <tr key={r.invoice_id} data-testid={`ledger-inv-${r.invoice_number}`}>
                                                <td className="hdb-mono font-semibold">{r.invoice_number}</td>
                                                <td className="hdb-mono">{r.invoice_date}</td>
                                                <td className="hdb-num hdb-num-right font-bold">{fmt(r.grand_total)}</td>
                                                <td className="hdb-num hdb-num-right text-emerald-700">{fmt(r.paid)}</td>
                                                <td className="hdb-num hdb-num-right font-bold" style={{color: paid ? "#065F46" : partial ? "#92400E" : "#991B1B"}}>
                                                    {fmt(r.balance)}
                                                </td>
                                                <td>
                                                    <span className={`hdb-tag ${paid ? "hdb-tag-mint" : partial ? "hdb-tag-gold" : "hdb-tag-red"}`}
                                                          style={!paid && !partial ? {background:"#FEE2E2",color:"#991B1B"} : {}}>
                                                        {paid ? "Paid" : partial ? "Partial" : "Unpaid"}
                                                    </span>
                                                </td>
                                                <td className="text-right whitespace-nowrap">
                                                    {paid ? (
                                                        <button onClick={() => setPaid(r.invoice_id, false)}
                                                                disabled={busyId === r.invoice_id}
                                                                className="hdb-btn hdb-btn-ghost text-xs"
                                                                data-testid={`ledger-unpaid-${r.invoice_number}`}>
                                                            {busyId === r.invoice_id
                                                                ? <Loader2 className="inline animate-spin" size={11}/>
                                                                : <><RotateCcw size={11}/> Unpaid</>}
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => setPaid(r.invoice_id, true)}
                                                                disabled={busyId === r.invoice_id}
                                                                className="hdb-btn hdb-btn-primary text-xs"
                                                                data-testid={`ledger-paid-${r.invoice_number}`}>
                                                            {busyId === r.invoice_id
                                                                ? <Loader2 className="inline animate-spin" size={11}/>
                                                                : <><Check size={11}/> Mark Paid</>}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Ledger entries (chronological) */}
                    {ledger && ledger.entries.length > 0 && (
                        <>
                            <div className="hdb-label mt-6 mb-2">Chronological entries {start || end ? "(filtered)" : "(all-time)"}</div>
                            <div className="hdb-card overflow-hidden">
                                <table className="hdb-table w-full">
                                    <thead><tr>
                                        <th>Date</th><th>Ref</th><th>Description</th>
                                        <th className="text-right">Debit</th>
                                        <th className="text-right">Credit</th>
                                        <th className="text-right">Balance</th>
                                    </tr></thead>
                                    <tbody>
                                        <tr className="bg-slate-50">
                                            <td colSpan={5} className="text-slate-500 text-xs italic">Balance carried forward</td>
                                            <td className="hdb-num hdb-num-right font-bold">{fmt(ledger.opening_balance)}</td>
                                        </tr>
                                        {ledger.entries.map((e, idx) => (
                                            <tr key={idx} className={e.type === "payment" ? "bg-emerald-50/60" : e.invoice_status === "cancelled" ? "bg-red-50/60 line-through" : ""}>
                                                <td className="hdb-mono text-xs">{e.date}</td>
                                                <td className="hdb-mono text-xs">{e.reference}</td>
                                                <td className="text-xs">{e.description}</td>
                                                <td className="hdb-num hdb-num-right">{e.debit ? fmt(e.debit) : ""}</td>
                                                <td className="hdb-num hdb-num-right text-emerald-700">{e.credit ? fmt(e.credit) : ""}</td>
                                                <td className="hdb-num hdb-num-right font-bold">{fmt(e.balance)}</td>
                                            </tr>
                                        ))}
                                        <tr className="bg-sky-50 font-bold">
                                            <td colSpan={3}>TOTALS</td>
                                            <td className="hdb-num hdb-num-right">{fmt(totals.debit)}</td>
                                            <td className="hdb-num hdb-num-right text-emerald-700">{fmt(totals.credit)}</td>
                                            <td className="hdb-num hdb-num-right">{fmt(totals.closing_balance)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function MiniKpi({ label, value, icon, tone = "slate", bold }) {
    const toneCls = {
        slate: "border-slate-200 text-slate-900",
        emerald: "border-emerald-300 text-emerald-800 bg-emerald-50",
        amber: "border-amber-300 text-amber-800 bg-amber-50",
    }[tone];
    return (
        <div className={`rounded-lg border-2 px-3 py-2 ${toneCls}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                {icon}{label}
            </div>
            <div className={`hdb-num mt-0.5 ${bold ? "text-xl font-black" : "text-base font-bold"}`}>{value}</div>
        </div>
    );
}
